import { Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { useState } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { User, UserRoles } from 'shared/models/user'

import { updateTeamUserGroups } from '../../api/core'

interface UserRoleChangeProps {
  record: User
  children: (handleRolesChange?: Handler) => React.ReactNode
  onSuccess?: Handler
}

export function UserRoleChange({ children, record, onSuccess }: UserRoleChangeProps) {
  const notify = useNotify()
  const refresh = useRefresh()

  const handleChange = (name: string) => (checked: boolean) => {
    // Check Admin if Business owner is checked
    if (name === 'businessOwnerChecked') {
      setState({ ...state, adminChecked: checked, [name]: checked })
    } else {
      setState({ ...state, [name]: checked })
    }
  }

  const setRoleCheckboxes = () => {
    setState({
      driverChecked: record?.roles?.includes(UserRoles.DRIVER) ?? false,
      adminChecked: record?.roles?.includes(UserRoles.TEAM_ADMIN) ?? false,
      businessOwnerChecked: record?.roles?.includes(UserRoles.BUSINESS_OWNER) ?? false,
    })
  }

  const handleUpdate = async (
    businessOwnerChecked: boolean,
    adminChecked: boolean,
    driverChecked: boolean
  ) => {
    const teamUserGroups = {
      team_user_groups: {
        BUSINESS_OWNER: businessOwnerChecked,
        TEAM_ADMIN: adminChecked,
        DRIVER: driverChecked,
      },
    }
    updateTeamUserGroups(record.id, teamUserGroups)
      .then(() => {
        setOpen(false)
        notify('Success!', { type: 'success' })
        refresh()
        onSuccess?.()
      })
      .catch((error: any) => {
        setOpen(false)
        setRoleCheckboxes()
        notify(JSON.stringify(error.response.data), { type: 'error' })
      })
  }

  const handleClick = () => {
    setRoleCheckboxes()
    setOpen(true)
  }

  const [state, setState] = useState({
    driverChecked: false,
    adminChecked: false,
    businessOwnerChecked: false,
  })

  const { driverChecked, adminChecked, businessOwnerChecked } = state
  const [open, setOpen] = useState(false)

  return (
    <>
      {children(handleClick)}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent>
          <Typography variant="h5"> Select the role(s) for this fleet member </Typography>
          <Checkbox
            name="driverChecked"
            value={driverChecked}
            onChange={handleChange('driverChecked')}
            label={<Text variant="label2">Driver</Text>}
          />
          <Text variant="paragraph2">
            <ul>
              <li>View and edit their own information</li>
              <li>View job details and submit proof of delivery</li>
              <li>View load prices for assigned jobs</li>
              <li>View load board and book loads</li>
            </ul>
          </Text>

          <Checkbox
            name="adminChecked"
            value={adminChecked}
            onChange={handleChange('adminChecked')}
            label={<Text variant="label2">Admin</Text>}
          />
          <Text variant="paragraph2">
            <ul>
              <li>View and edit fleet and driver information</li>
              <li>Book loads for drivers</li>
              <li>View fleet performance and revenue</li>
              <li>Manage fleet's CT Cash account</li>
              <li>Request Pickup Pay</li>
            </ul>
          </Text>

          <Checkbox
            name="businessOwnerChecked"
            value={businessOwnerChecked}
            onChange={handleChange('businessOwnerChecked')}
            label={<Text variant="label2">Business Owner</Text>}
          />
          <Text variant="paragraph2">
            <ul>
              <li>Includes all admin permissions</li>
              <li>Responsible for signing CT lease agreement & insurance documents</li>
            </ul>
          </Text>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleUpdate(businessOwnerChecked, adminChecked, driverChecked)}
          >
            Confirm
          </Button>
          <Button variant="text" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
